import { track } from "@/features/analytics";
import type { IMediaFileMetadata, IMediaUploadResponse } from "@/shared/types";
import { useLazyGetMediaByHashQuery, useUploadMediaMutation } from "@api/media";
import { useLazyGetSignedPostPolicyQuery } from "@api/versions";
import { environment } from "@environment";
import { normalizeFontMimeType } from "@/shared/utils/font-mime-type";
import { sha256Hex } from "@/shared/utils/sha256";
import { useAppSelector } from "@store";
import { isOptimizeVideoEnabled } from "@store/account/selectors";
import { useState } from "react";
import uuid from "react-uuid";

const dataUrlToBlob = (dataUrl: string): { blob: Blob; type: string } => {
  const [header, data] = dataUrl.split("?")[0].split(",");
  const mimeMatch = header.match(/data:([^;]+)/);
  const type = mimeMatch ? mimeMatch[1] : "application/octet-stream";

  const byteString = atob(data);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([arrayBuffer], { type });
  return { blob, type };
};

interface IUploadData {
  blob: Blob | File;
  signal?: AbortSignal;
  title?: string;
  fileType: string;
  typeGroup?: string;
  folderId?: string | null;
  metadata?: Partial<IMediaFileMetadata>;
  optimizeVideoEnabled?: boolean;
}

export const useUploadMedia = () => {
  const [loading, setLoading] = useState(false);
  const [getSignedPostPolicy] = useLazyGetSignedPostPolicyQuery();
  const [uploadMedia] = useUploadMediaMutation();
  const [getMediaByHash] = useLazyGetMediaByHashQuery();
  const optimizeVideoEnabled = useAppSelector(isOptimizeVideoEnabled);

  const handleUploadMedia = async ({
    blob,
    signal,
    title,
    fileType: rawFileType,
    typeGroup,
    folderId,
    metadata,
    optimizeVideoEnabled,
  }: IUploadData): Promise<IMediaUploadResponse> => {
    // A font picked in the browser arrives with a legacy/vendor alias
    // (`application/font-woff`, `application/x-font-ttf`, …). Canonicalising it
    // before it reaches the signed-upload token keeps the stored Content-Type
    // and the derived `typeGroup` correct — `application` is not a media kind
    // the library knows how to render.
    const fileType = normalizeFontMimeType(rawFileType);

    try {
      setLoading(true);

      // Layer 3: reuse an existing identical asset instead of re-uploading.
      // Any backend error is treated as a miss so uploads never get blocked.
      let contentHash: string | undefined;
      try {
        contentHash = await sha256Hex(blob);
        const existing = await getMediaByHash(contentHash).unwrap();
        if (existing) {
          setLoading(false);
          return existing;
        }
      } catch {
        // ignore — fall through to a normal upload
      }

      const {
        data: { url: signeUrl, filename: bucketFileName },
      } = await getSignedPostPolicy(
        {
          type: fileType,
          bucket: environment.files,
          queryKey: uuid(),
        },
        false,
      );

      const formData = new FormData();

      Object.entries({ ...signeUrl.fields, file: blob }).forEach(
        ([key, value]) => {
          formData.append(key, value as string | Blob);
        },
      );

      await fetch(signeUrl.url, {
        method: "POST",
        body: formData,
      });
      const originalUrl = `https://${environment.assetsBucket}/${bucketFileName}`;
      const url = originalUrl;
      const fileMetadata: { size: number; type: string; typeGroup: string } = {
        size: blob.size,
        type: fileType,
        typeGroup: typeGroup || fileType.split("/")[0],
      };

      return await uploadMedia({
        signal,
        title: title || uuid(),
        type: fileMetadata.type,
        typeGroup: fileMetadata.typeGroup,
        size: fileMetadata.size,
        url,
        contentHash,
        folderId,
        metadata,
        optimizeVideoEnabled,
      }).unwrap();
    } catch (e) {
      track("failed to upload media", { error: e });
      setLoading(false);
      throw new Error("Failed to upload media");
    }
    setLoading(false);
  };

  const uploadFromUrl = async (
    url: string,
    data: Partial<IUploadData> = {},
  ): Promise<IMediaUploadResponse> => {
    try {
      let blob: Blob;
      let fileType = "";

      if (url.startsWith("data:")) {
        const { blob: dataBlob, type } = dataUrlToBlob(url);
        blob = dataBlob;
        fileType = type;
      } else {
        let parsedUrl = url;
        if (url && url.indexOf("stories.storydoc.com") > -1) {
          parsedUrl = url.replace("stories.storydoc.com", "www.storydoc.com");
        }

        const urlResp = await fetch(parsedUrl);
        blob = await urlResp.blob();
        fileType = blob.type || "";

        if (!fileType && parsedUrl.includes(".mp4")) {
          fileType = "video/mp4";
        }

        if (!fileType && parsedUrl.includes(".webm")) {
          fileType = "video/webm";
        }
      }

      return await handleUploadMedia({
        fileType,
        blob,
        optimizeVideoEnabled,
        ...data,
      });
    } catch (e) {
      track("failed to upload media from url", { error: e, url });
      setLoading(false);
      throw new Error("Failed to upload media from url");
    }
    setLoading(false);
  };

  return {
    loading,
    uploadMedia: handleUploadMedia,
    uploadMediaFromUrl: uploadFromUrl,
  };
};
